/**
 * SubscribeToCommunityLiveStreamUseCase
 *
 * Subscribe to the single live message's liveness listener, surfacing a typed
 * `LiveStreamStats` for EVERY raw doc change — `isLive` (for in-session end
 * detection) + `watchers` (for the count display) + `broadcasterStreamingId` +
 * `coins` (the coins pill) + `likes` (the else-if chain discriminator) +
 * `latestLiveGiftInfo` (the last-gift overlay), tagged with the raw
 * `changeType`. A thin single-action passthrough to
 * `LiveStreamingService.subscribeToLiveMessage`; the returned `Unsubscribe` is
 * handed straight back for the caller to own. All fields are passed through
 * verbatim — the `watchers → likes → coins` else-if selection and the last-gift
 * trigger stay in the consumer (Task-4 hook), matching Flutter.
 *
 * Web analog of Flutter's `live_streaming_screen.subscribeToBroadcastingMessageStats`
 * (lib/presentation/screens/community/live_streaming_screen.dart:704-757). The
 * raw `changeType` is surfaced verbatim (incl. `removed`): the `removed`-skip
 * (`:716-718`), the audience-only `!_isBroadcaster && !isLive` offline teardown
 * (`:723-727`), and the watcher-count update (`:729-731`) are the consumer's
 * (Task-10 hook) concern, NOT this use case's — matching Flutter, where those
 * gates run in the SCREEN, not the data hop.
 *
 * Constructor-injected service (video-call use-case pattern) for testability.
 */

import type { Unsubscribe } from 'firebase/firestore';
import {
  LiveStreamingService,
  liveStreamingService,
  type LiveStreamStats,
} from '@/domain/services/liveStreaming/liveStreamingService';

export interface SubscribeToCommunityLiveStreamParams {
  /** The live message `id` the liveness listener queries. */
  liveStreamingMessageId: string;
  /** Invoked with a typed `LiveStreamStats` for every raw doc change. */
  onStats: (stats: LiveStreamStats) => void;
  /** Optional listener-error callback. */
  onError?: (error: Error) => void;
}

export class SubscribeToCommunityLiveStreamUseCase {
  private service: LiveStreamingService;

  constructor(service: LiveStreamingService = liveStreamingService) {
    this.service = service;
  }

  /**
   * Start the liveness subscription.
   *
   * @returns The service's `Unsubscribe` handle, propagated verbatim — the
   *   consumer (Task-10 hook) owns cleanup.
   */
  execute(params: SubscribeToCommunityLiveStreamParams): Unsubscribe {
    const { liveStreamingMessageId, onStats, onError } = params;
    return this.service.subscribeToLiveMessage(liveStreamingMessageId, onStats, onError);
  }
}

// Export singleton instance
export const subscribeToCommunityLiveStreamUseCase = new SubscribeToCommunityLiveStreamUseCase();
